import React, { forwardRef, useState, useEffect } from 'react';
import { Dropdown } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBold,
    faItalic,
    faUnderline,
    faListOl,
    faListUl,
    faAlignLeft,
    faAlignCenter,
    faAlignRight,
    faAlignJustify,
    faLink,
    faUndo,
} from '@fortawesome/free-solid-svg-icons';


import { AntdButton } from './InputField';
import { cleanPastedHtml } from './cleanPastedHtml';

const alignmentIcons = {
    justifyLeft: <FontAwesomeIcon icon={faAlignLeft} />,
    justifyCenter: <FontAwesomeIcon icon={faAlignCenter} />,
    justifyRight: <FontAwesomeIcon icon={faAlignRight} />,
    justifyFull: <FontAwesomeIcon icon={faAlignJustify} />,
};


const alignmentOptions = [
    { key: 'justifyLeft', label: 'Align Left', icon: <FontAwesomeIcon icon={faAlignLeft} /> },
    { key: 'justifyCenter', label: 'Align Center', icon: <FontAwesomeIcon icon={faAlignCenter} /> },
    { key: 'justifyRight', label: 'Align Right', icon: <FontAwesomeIcon icon={faAlignRight} /> },
    { key: 'justifyFull', label: 'Justify', icon: <FontAwesomeIcon icon={faAlignJustify} /> },
];

const alignmentMenuItems = alignmentOptions.map((opt) => ({
    key: opt.key,
    icon: opt.icon,
    label: opt.label,
}));

const FONT_FAMILIES = [
    'Calibri',
    'Arial',
    'Georgia',
    'Verdana',
    'Tahoma',
    'Courier New',
    'Times New Roman',
    'Trebuchet MS',
    'Impact',
];

const FONT_SIZES = [8, 9, 10, 11, 12, 14, 16, 18, 20, 22, 24, 28, 32, 36];

const getSelectionParentSpan = () => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return null;
    let node = selection.anchorNode;
    while (node && node !== document.body) {
        if (node.nodeType === 1 && node.tagName === 'SPAN') return node;
        node = node.parentNode;
    }
    return null;
};

const cleanNestedSpans = (element) => {
    if (!element) return;
    // Merge nested spans with identical style
    const spans = element.querySelectorAll('span');
    spans.forEach((span) => {
        if (
            span.parentNode &&
            span.parentNode.tagName === 'SPAN' &&
            span.style.cssText === span.parentNode.style.cssText
        ) {
            // Move children out of nested span
            while (span.firstChild) {
                span.parentNode.insertBefore(span.firstChild, span);
            }
            span.parentNode.removeChild(span);
        }
    });
};

const RichTextEditor = forwardRef(({ onChange, defaultValue,label,value }, bodyRef) => {
    const [currentFont, setCurrentFont] = useState('Calibri');
    const [currentFontSize, setCurrentFontSize] = useState('12');
    const [currentColor, setCurrentColor] = useState('#000000');
    const [currentAlignment, setCurrentAlignment] = useState('justifyLeft');

    // Dropdown-like Alignment Styles (inside component)
const [showAlignMenu, setShowAlignMenu] = useState(false);

useEffect(() => {
    const handleClickOutside = (event) => {
        // If the dropdown is open AND click is outside, close it
        if (showAlignMenu && !event.target.closest('.alignment-dropdown-container')) {
            setShowAlignMenu(false);
        }
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
        document.removeEventListener('click', handleClickOutside);
    };
}, [showAlignMenu]);

const [hover, setHover] = useState(null);


const alignmentDropdownStyle = {
    position: "absolute",
    marginTop: "4px",
    background: "#fff",
    border: "1px solid #d9d9d9",
    borderRadius: "6px",
    padding: "4px 0",  // more padding for better size
    display: "flex",
    flexDirection: "column",
    zIndex: 999,
    minWidth: "100px",   // slightly bigger width
    boxShadow: "0 2px 6px rgba(0,0,0,0.15)", // subtle shadow like other buttons
};

const formatBtnStyle = {
    border: "none",
    background: "transparent",
    padding: "6px 10px",   // same height as toolbar buttons
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    color: "#000",
    fontSize: "14px",
    width: "100%",
    textAlign: "left",
    gap: "6px",
};

const formatBtnHoverStyle = {
    background: "#f0f0f0",
};

    // Update font family, size, color selects based on cursor or selection changes
    const updateSelectionStyles = () => {
        const selection = window.getSelection();
        if (!selection.rangeCount) return;
        const span = getSelectionParentSpan();
        if (span) {
            const computed = window.getComputedStyle(span);
            setCurrentFont(computed.fontFamily.replace(/['"]/g, '').split(',')[0]);
            setCurrentFontSize(parseInt(computed.fontSize));
            setCurrentColor(rgbToHex(computed.color));
        } else {
            // No span parent: reset to default or do nothing
        }
    };

    // Converts rgb(0,0,0) to #000000 hex
    const rgbToHex = (rgb) => {
        const result = /^rgba?\((\d+),\s*(\d+),\s*(\d+)/i.exec(rgb);
        return result
            ? '#' +
            [1, 2, 3]
                .map((n) => {
                    const hex = parseInt(result[n]).toString(16);
                    return hex.length === 1 ? '0' + hex : hex;
                })
                .join('')
            : rgb;
    };

    useEffect(() => {
        document.addEventListener('selectionchange', updateSelectionStyles);
        return () => {
            document.removeEventListener('selectionchange', updateSelectionStyles);
        };
    }, []);

    const applyStyleToSelection = (newStyles) => {
        const selection = window.getSelection();
        if (!selection.rangeCount || selection.isCollapsed) return;

        const range = selection.getRangeAt(0);
        const fragment = range.cloneContents();

        const extractInlineStyles = (node) => {
            if (node.nodeType === 1 && node.tagName === 'SPAN') {
                return {
                    fontFamily: node.style.fontFamily || undefined,
                    fontSize: node.style.fontSize || undefined,
                    color: node.style.color || undefined,
                };
            }
            return {};
        };

        let existingInlineStyles = {};
        const walker = document.createTreeWalker(fragment, NodeFilter.SHOW_ELEMENT, null, false);
        let spanFound = false;

        while (walker.nextNode()) {
            const node = walker.currentNode;
            if (node.tagName === 'SPAN') {
                existingInlineStyles = extractInlineStyles(node);
                spanFound = true;
                break;
            }
        }

        if (!spanFound) {
            const parentSpan = getSelectionParentSpan();
            if (parentSpan) {
                existingInlineStyles = extractInlineStyles(parentSpan);
            }
        }

        const combinedStyles = { ...existingInlineStyles, ...newStyles };

        const applyStylesToNode = (node) => {
            if (node.nodeType === 3) {
                const span = document.createElement('span');
                Object.entries(combinedStyles).forEach(([key, value]) => {
                    if (value) span.style[key] = value;
                });
                span.textContent = node.textContent;
                return span;
            } else if (node.nodeType === 1) {
                const clone = node.cloneNode(false);
                node.childNodes.forEach((child) => {
                    clone.appendChild(applyStylesToNode(child));
                });
                return clone;
            }
            return node.cloneNode(true);
        };

        const styledFragment = document.createDocumentFragment();
        fragment.childNodes.forEach((node) => {
            styledFragment.appendChild(applyStylesToNode(node));
        });

        range.deleteContents();
        range.insertNode(styledFragment);

        cleanNestedSpans(bodyRef.current);
    };

   const formatText = (command, value = null) => {
    if (!bodyRef.current) return;

    // required: editor must have focus before formatting
    bodyRef.current.focus();

    // FONT FAMILY
    if (command === "fontName") {
        applyStyleToSelection({ fontFamily: value });
        setCurrentFont(value);
        return;
    }

    // FONT SIZE
    if (command === "fontSize") {
        applyStyleToSelection({ fontSize: `${value}px` });
        setCurrentFontSize(value);
        return;
    }

    // FONT COLOR
    if (command === "foreColor") {
        applyStyleToSelection({ color: value });
        setCurrentColor(value);
        return;
    }

    // ALIGNMENT IS HANDLED SEPARATELY
    if (command.includes("justify")) return;

    // ⭐ NORMAL FORMATTING (bold, italic, lists, underline)
    document.execCommand(command, false, value);
};


   const insertLink = () => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    // Save the current selection range
    const range = selection.getRangeAt(0);

    const url = prompt("Enter URL:");
    if (!url) return;

    // Restore the selection before execCommand
    selection.removeAllRanges();
    selection.addRange(range);

    // Focus the editor
    bodyRef.current.focus();

    // Create the link
    document.execCommand("createLink", false, url);
};


const handleAlignment = (key) => {
    if (!bodyRef.current) return;

    bodyRef.current.focus();

    try {
        if (key === "justifyLeft") document.execCommand("justifyLeft");
        if (key === "justifyCenter") document.execCommand("justifyCenter");
        if (key === "justifyRight") document.execCommand("justifyRight");
        if (key === "justifyFull") document.execCommand("justifyFull");
    } catch (e) {
        // ignore any browser popup alerts silently
    }

    setCurrentAlignment(key);
};

const [history, setHistory] = useState([]);
const [historyStep, setHistoryStep] = useState(-1);

const handleBodyChange = (html) => {
    onChange && onChange(html);

    setHistory(prev => {
        const newHistory = [...prev.slice(0, historyStep + 1), html];
        setHistoryStep(newHistory.length - 1);
        return newHistory;
    });
};

// Clean pasted content so foreign spacing/classes from Word/Outlook/etc.
// don't poison the editor. Only fires on paste — typing, toolbar
// formatting, and programmatic innerHTML writes are untouched.
const handlePaste = (e) => {
    const cd = e.clipboardData || window.clipboardData;
    if (!cd) return;
    const html = cd.getData('text/html');
    // If clipboard has no HTML (plain text or image), let default behavior happen.
    if (!html) return;
    e.preventDefault();
    const cleaned = cleanPastedHtml(html);
    if (cleaned) {
        // execCommand integrates with the contentEditable undo stack.
        document.execCommand('insertHTML', false, cleaned);
        if (bodyRef.current) handleBodyChange(bodyRef.current.innerHTML);
    } else {
        // Fall back to plain text if cleaning emptied everything.
        const text = cd.getData('text/plain') || '';
        if (text) document.execCommand('insertText', false, text);
    }
};
const undo = () => {
    if (historyStep > 0) {
        const newStep = historyStep - 1;
        setHistoryStep(newStep);
        bodyRef.current.innerHTML = history[newStep];
    }
};
    const resetFormatting = () => {
        if (!bodyRef.current) return;
        const plainText = bodyRef.current.innerText || '';
        bodyRef.current.innerHTML = plainText;
        setCurrentFont('Calibri');
        setCurrentFontSize('12');
        setCurrentColor('#000000');
        setCurrentAlignment('justifyLeft');
        onChange && onChange(plainText);
    };

    useEffect(() => {
    if (bodyRef.current && defaultValue) {
        bodyRef.current.innerHTML = defaultValue;
    }
}, [defaultValue]);

useEffect(() => {
    if (bodyRef.current && value !== undefined) {
        if (bodyRef.current.innerHTML !== value) {
            bodyRef.current.innerHTML = value;
        }
    }
}, [value]);


    return (
        <div>
            
            <div className="toolbar" style={{ marginBottom: 10 }}>
                <select
                    value={currentFont}
                    onChange={(e) => formatText('fontName', e.target.value)}
                    className="font-select"
                    style={{ marginRight: 8 }}
                >
                    {FONT_FAMILIES.map((font) => (
                        <option key={font} value={font}>
                            {font}
                        </option>
                    ))}
                </select>

                <select
                    value={currentFontSize}
                    onChange={(e) => formatText('fontSize', e.target.value)}
                    className="size-select"
                    style={{ marginRight: 8, width: 60 }}
                >
                    {FONT_SIZES.map((size) => (
                        <option key={size} value={size}>
                            {size}
                        </option>
                    ))}
                </select>

                <input
                    type="color"
                    value={currentColor}
                    onChange={(e) => formatText('foreColor', e.target.value)}
                    style={{
                        width: 32,
                        height: 32,
                        padding: 2,
                        border: '1px solid #ccc',
                        borderRadius: 4,
                        cursor: 'pointer',
                        marginRight: 8,
                        verticalAlign: 'middle',
                        WebkitAppearance: 'none',
                        appearance: 'none',
                        backgroundColor: currentColor,
                    }}
                    title="Font Color"
                />

                <AntdButton className="format-btn" onClick={() => formatText('bold')} label={<FontAwesomeIcon icon={faBold} />} />
                <AntdButton className="format-btn" onClick={() => formatText('italic')} label={<FontAwesomeIcon icon={faItalic} />} />
                <AntdButton className="format-btn" onClick={() => formatText('underline')} label={<FontAwesomeIcon icon={faUnderline} />} />
                <AntdButton className="format-btn" onClick={() => formatText('insertUnorderedList')} label={<FontAwesomeIcon icon={faListUl} />} />
                <AntdButton className="format-btn" onClick={() => formatText('insertOrderedList')} label={<FontAwesomeIcon icon={faListOl} />} />

            <div className="alignment-dropdown-container" style={{ position: "relative", display: "inline-block" }}>
    <AntdButton
        className="format-btn"
        style={{ padding: "6px 10px" }}
        onClick={() => setShowAlignMenu(!showAlignMenu)}
        label={<FontAwesomeIcon icon={faAlignLeft} />}
    />

    {showAlignMenu && (
        <div style={alignmentDropdownStyle}>
            <button
                style={{ ...formatBtnStyle, ...(hover === "left" ? formatBtnHoverStyle : {}) }}
                onMouseEnter={() => setHover("left")}
                onMouseLeave={() => setHover(null)}
                onClick={() => { handleAlignment("justifyLeft"); setShowAlignMenu(false); }}
            >
                <FontAwesomeIcon icon={faAlignLeft} /> Left
            </button>

            <button
                style={{ ...formatBtnStyle, ...(hover === "center" ? formatBtnHoverStyle : {}) }}
                onMouseEnter={() => setHover("center")}
                onMouseLeave={() => setHover(null)}
                onClick={() => { handleAlignment("justifyCenter"); setShowAlignMenu(false); }}
            >
                <FontAwesomeIcon icon={faAlignCenter} /> Center
            </button>

            <button
                style={{ ...formatBtnStyle, ...(hover === "right" ? formatBtnHoverStyle : {}) }}
                onMouseEnter={() => setHover("right")}
                onMouseLeave={() => setHover(null)}
                onClick={() => { handleAlignment("justifyRight"); setShowAlignMenu(false); }}
            >
                <FontAwesomeIcon icon={faAlignRight} /> Right
            </button>

            <button
                style={{ ...formatBtnStyle, ...(hover === "justify" ? formatBtnHoverStyle : {}) }}
                onMouseEnter={() => setHover("justify")}
                onMouseLeave={() => setHover(null)}
                onClick={() => { handleAlignment("justifyFull"); setShowAlignMenu(false); }}
            >
                <FontAwesomeIcon icon={faAlignJustify} /> Justify
            </button>
        </div>
    )}
</div>

                <AntdButton className="format-btn" onClick={insertLink} label={<FontAwesomeIcon icon={faLink} />} />
                <AntdButton className="format-btn" onClick={undo} label={<FontAwesomeIcon icon={faUndo} />} />

            </div>
             {label && (
          <label
            style={{
              display: "block",
              marginBottom: 6,
              fontWeight: 500,
            }}
          >
            {label}
          </label>
        )}

            <div
                ref={bodyRef}
                label={label}
                value={value}
                contentEditable
                suppressContentEditableWarning={true}
                className="editor"
                onInput={(e) => handleBodyChange(e.currentTarget.innerHTML)}
                onPaste={handlePaste}
                style={{
                    border: '1px solid #ddd',
                    padding: 10,
                    borderRadius: 4,
                    outline: 'none',
                    fontFamily: 'Calibri', // Optional: default styling for initial typing
                    fontSize: '12pt',
                    color: '#000',
textAlign:
    currentAlignment === "justifyLeft" ? "left" :
    currentAlignment === "justifyCenter" ? "center" :
    currentAlignment === "justifyRight" ? "right" :
    currentAlignment === "justifyFull" ? "justify" :
    "left"

                }}
                
            />
        </div>
    );
});

export default RichTextEditor;
